import {
    PasswordRequirementsPopover,
    RhCheckbox,
    RhFormWrapper,
    RhTabsWrapper,
    RHTextField,
    useRhCustomContext,
} from '../../../../../dum';
import { Alert, TextField } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import * as yup from 'yup';
import { SchemaOf, ValidationError } from 'yup';
import {
    ActivityEntityName,
    queryIsEmailOccupied,
    RoleId,
    useCreateUserMutation,
    useCurrentUserQuery,
    useResetUserPasswordMutation,
    useUpdateUserMutation,
} from '../../../../../../services';
import {
    evaluatePassword,
    isPasswordValid,
} from '../../../../../../services/auth/password-policy';
import { useHistorialTab } from '../../../../historial';
import { useApolloClient } from '@apollo/client';
import { RolesInput } from './inputs/roles-input';

// The single Spanish message for a password that fails the policy. The live
// requirements popup shows which specific rule is unmet; this is the fallback the
// form shows once the field is touched/submitted.
const PASSWORD_POLICY_MESSAGE =
    'La contraseña debe tener al menos 10 caracteres, una letra, un número y un símbolo';

export interface IUser {
    id?: number | null;
    first_name: string;
    last_name: string;
    email: string;
    // Phase 3: when true, this account must pass an emailed MFA code on every
    // login. Off means the password-only path.
    mfa_enabled: boolean;
    // Blocks login for the account without deleting it. Editable via a checkbox.
    login_disabled: boolean;
    // Read-only (DB-only flag, on no input): a root account is editable only by
    // itself. Used to lock the form for everyone else — never sent in a mutation.
    is_root: boolean;
    roles: { id: number }[];
}

export interface IUserWithPassword extends IUser {
    password_1?: string;
    password_2?: string;
    // Form-only, edit + super-user only: checking it and saving forces the target
    // to choose a new password at next login and kills their live sessions. It is
    // an action, not stored state (unlike `mfa_enabled`), so it always starts
    // unchecked and is applied via the `resetUserPassword` mutation on submit.
    force_password_reset: boolean;
}

interface IUserForm {
    user?: IUser | null;
    onSuccess: () => void;
    disabled?: boolean;
}

// A password input with the live requirements popup. Local to this form (the only
// react-hook-form consumer of the policy); the forced-change login screen wires
// the same popover against its own plain inputs. Reads the value straight from
// the field so the popup updates on every keystroke, and owns its own focus state
// so the popup shows only while the field is focused.
function PasswordField({ name, label }: { name: string; label: string }) {
    const { control } = useFormContext();
    const { field, fieldState } = useController({ name, control });
    const { disabled } = useRhCustomContext();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const value: string = field.value || '';

    return (
        <>
            <TextField
                sx={{ my: 2, width: '100%' }}
                label={label}
                type={'password'}
                name={name}
                value={value}
                inputRef={field.ref}
                onChange={field.onChange}
                onFocus={(e) => setAnchorEl(e.currentTarget)}
                onBlur={() => {
                    field.onBlur();
                    setAnchorEl(null);
                }}
                autoComplete={'new-password'}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message || ''}
                margin={'dense'}
                size={'small'}
                disabled={disabled}
            />
            <PasswordRequirementsPopover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                rules={evaluatePassword(value)}
            />
        </>
    );
}

export function UserForm({ user, onSuccess, disabled = false }: IUserForm) {
    const [createMutation, { loading: createLoading }] =
        useCreateUserMutation();
    const [updateMutation, { loading: updateLoading }] =
        useUpdateUserMutation();
    const [resetPasswordMutation] = useResetUserPasswordMutation();

    // Only a super user may force a password reset. Hiding the checkbox for
    // everyone else just avoids a dead control — the mutation is RoleId.SUPER
    // gated on the server regardless.
    const { data: currentUserData } = useCurrentUserQuery();
    const isSuperUser =
        !!currentUserData &&
        currentUserData.currentUser.role_ids.includes(RoleId.Super);
    // A reset only makes sense for an existing account (a new user is given a
    // password directly), so it is edit-only as well as super-only.
    const canForceReset = isSuperUser && !!user && !!user.id;
    // A root account may be edited only by itself. For any other user the whole
    // form is read-only (the backend enforces the same rule; this just avoids a
    // confusing Forbidden on save). A root editing its own record is unaffected.
    const currentUserId = currentUserData?.currentUser?.id;
    const isRootLockedForMe = !!user?.is_root && currentUserId !== user.id;

    const apolloClient = useApolloClient();

    const historialTab = useHistorialTab({
        entityName: ActivityEntityName.User,
        entityId: user?.id,
        unsavedMessage:
            'El historial aparece una vez que el usuario ha sido guardado.',
        emptyMessage: 'No hay actividades registradas para este usuario.',
    });

    const initialValues = useMemo<IUserWithPassword>(() => {
        return {
            id: user?.id || undefined,
            first_name: user?.first_name || '',
            last_name: user?.last_name || '',
            email: user?.email || '',
            mfa_enabled: user?.mfa_enabled ?? false,
            login_disabled: user?.login_disabled ?? false,
            is_root: user?.is_root ?? false,
            password_1: '',
            password_2: '',
            force_password_reset: false,
            roles: user ? user.roles : [],
        };
    }, [user]);

    const validationSchema: SchemaOf<IUserWithPassword> = yup.object({
        id: yup.number().notRequired().nullable(),
        first_name: yup.string().required(`'Nombre' es obligatorio`),
        // Last name is optional: `.defined()` allows an empty string (blank is
        // fine) while still satisfying the non-optional `string` on IUser. The
        // backend stores '' and builds `fullname` from the parts present.
        last_name: yup.string().defined(),
        mfa_enabled: yup.boolean().required(),
        login_disabled: yup.boolean().required(),
        // Passive in the form model (read-only, never rendered or sent); present
        // only because SchemaOf requires a key for every field on the interface.
        is_root: yup.boolean().required(),
        force_password_reset: yup.boolean().required(),
        roles: yup
            .array()
            .of(
                yup.object().shape({
                    id: yup.number().required(),
                }),
            )
            .required(),
        email: yup
            .string()
            .required(`'Email' es obligatorio`)
            .test('unique', async function (value, context): Promise<
                boolean | ValidationError
            > {
                if (!!value) {
                    const result = await queryIsEmailOccupied(apolloClient, {
                        Email: value,
                        UserId: user?.id || null,
                    });
                    if (result.data.isEmailOccupied) {
                        return context.createError({
                            message: `El email ${value} se encuentra en uso`,
                        });
                    }
                    return true;
                } else {
                    return true;
                }
            }),
        password_1: yup.string().when('id', {
            is: (val: any) => !val,
            // Create: a password is mandatory and must satisfy the full policy.
            then: (schema) =>
                schema
                    .required(`'Contraseña' es obligatorio`)
                    .test(
                        'policy',
                        PASSWORD_POLICY_MESSAGE,
                        (val) => isPasswordValid(val || ''),
                    ),
            // Edit: blank leaves the password unchanged; a non-blank value must
            // meet the same policy.
            otherwise: (schema) =>
                schema.notRequired().test((val: any, ctx: any) => {
                    if (val !== '' && val != null && !isPasswordValid(val)) {
                        return ctx.createError({
                            message: PASSWORD_POLICY_MESSAGE,
                        });
                    }
                    return true;
                }),
        }),
        password_2: yup
            .string()
            .oneOf(
                [yup.ref('password_1'), null],
                `Las contraseñas deben de coincidir`,
            ),
    });

    return (
        <RhFormWrapper
            validationSchema={validationSchema}
            initialValues={initialValues}
            disabled={
                createLoading ||
                updateLoading ||
                disabled ||
                isRootLockedForMe
            }
            onSubmit={async (data) => {
                try {
                    if (!user) {
                        await createMutation({
                            variables: {
                                CreateUserInput: {
                                    email: data.email,
                                    first_name: data.first_name,
                                    last_name: data.last_name,
                                    password: data.password_1!,
                                    mfa_enabled: data.mfa_enabled,
                                    login_disabled: data.login_disabled,
                                    roles: data.roles,
                                },
                            },
                        });
                    } else if (user && user.id) {
                        await updateMutation({
                            variables: {
                                UpdateUserInput: {
                                    id: user.id,
                                    first_name: data.first_name,
                                    last_name: data.last_name,
                                    email: data.email,
                                    mfa_enabled: data.mfa_enabled,
                                    login_disabled: data.login_disabled,
                                    password:
                                        data.password_1 !== undefined &&
                                        data.password_1 !== null &&
                                        data.password_1 !== ''
                                            ? data.password_1
                                            : undefined,
                                    roles: data.roles.map((role) => {
                                        return {
                                            id: role.id,
                                        };
                                    }),
                                },
                            },
                        });
                        // Applied after the profile update: it revokes the
                        // target's sessions and flags a forced change at next
                        // login. Gated to super users editing an existing account
                        // (canForceReset), so a non-super submit can never trigger
                        // it even if the field were somehow set.
                        if (canForceReset && data.force_password_reset) {
                            await resetPasswordMutation({
                                variables: { UserId: user.id },
                            });
                        }
                    }
                    onSuccess();
                } catch (e) {
                    console.error(e);
                }
            }}
            onInvalid={async (errors) => {
                console.error(errors);
            }}
        >
            {/*
              Converted from bare fields to tabs so the admin-only Historial tab
              has somewhere to live. With one content tab the UI is materially
              unchanged for non-admins.
            */}
            <RhTabsWrapper
                tabs={[
                    {
                        label: 'General',
                        fields: [
                            'first_name',
                            'last_name',
                            'email',
                            'roles',
                            'mfa_enabled',
                            'login_disabled',
                            'force_password_reset',
                            'password_1',
                            'password_2',
                        ],
                        content: (
                            <>
                                {isRootLockedForMe ? (
                                    <Alert
                                        severity={'info'}
                                        sx={{ mb: 1 }}
                                    >
                                        Esta cuenta raíz solo puede ser
                                        modificada por sí misma.
                                    </Alert>
                                ) : null}
                                <RHTextField
                                    name={'first_name'}
                                    label={'Nombre'}
                                />
                                <RHTextField
                                    name={'last_name'}
                                    label={'Apellidos'}
                                />
                                <RHTextField name={'email'} label={'Email'} />
                                <RolesInput />
                                <RhCheckbox
                                    name={'mfa_enabled'}
                                    label={
                                        'Requiere verificación por correo (MFA)'
                                    }
                                />
                                <RhCheckbox
                                    name={'login_disabled'}
                                    label={
                                        'Deshabilitar acceso (bloquear inicio de sesión)'
                                    }
                                    helperText={
                                        'El usuario no podrá iniciar sesión y sus sesiones activas se cerrarán. La cuenta no se elimina.'
                                    }
                                />
                                {canForceReset ? (
                                    <RhCheckbox
                                        name={'force_password_reset'}
                                        label={
                                            'Forzar cambio de contraseña en el próximo inicio de sesión'
                                        }
                                        helperText={
                                            'Cierra las sesiones activas del usuario y le exige elegir una nueva contraseña al iniciar sesión.'
                                        }
                                    />
                                ) : null}
                                <PasswordField
                                    name={'password_1'}
                                    label={'Contraseña'}
                                />
                                <RHTextField
                                    name={'password_2'}
                                    label={'Confirmación de la contraseña'}
                                    type={'password'}
                                />
                            </>
                        ),
                    },
                    // Empty for non-admins, so they get no tab at all. The
                    // snapshot behind it never contains password or
                    // remember_token — see getUserSnapshot in user.service.ts.
                    ...historialTab,
                ]}
            />
        </RhFormWrapper>
    );
}
